/**
 * Real-time microphone audio level hook.
 *
 * Uses the Web Audio API (AudioContext → MediaStreamSourceNode → AnalyserNode)
 * to drive a live 0–1 normalised level value from the actual microphone input.
 * When `enabled` is false (or the component unmounts) the stream and context
 * are torn down immediately so the browser mic indicator disappears.
 *
 * @module useMicAudioLevel
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { logger } from '@/lib/logger';

export interface MicAudioLevelState {
  /** Normalised amplitude 0–1 from the most recent analyser frame */
  level: number;
  /** True when the stream + analyser loop are running */
  isActive: boolean;
  /** Non-null when getUserMedia or AudioContext setup failed */
  error: string | null;
}

/**
 * Pure helper: derive a normalised 0–1 level from a frequency-data array.
 *
 * Computes the RMS of the per-bin magnitudes (each 0–255) and scales it up so
 * typical speech (RMS ~0.05–0.15) maps to a clearly visible range. Exported so
 * it can be unit-tested without a Web Audio runtime.
 */
export function computeMicLevel(data: ArrayLike<number>): number {
  const len = data.length;
  if (len === 0) return 0;
  let sum = 0;
  for (let i = 0; i < len; i++) {
    const v = data[i] / 255;
    sum += v * v;
  }
  const rms = Math.sqrt(sum / len);
  return Math.min(1, rms * 5);
}

/**
 * Hook that opens a getUserMedia stream and drives a live audio-level value.
 *
 * @param enabled - When false the stream is torn down immediately.
 * @returns Current level, active state, and any error message.
 *
 * @example
 * ```tsx
 * const { level, isActive } = useMicAudioLevel(permissionGranted);
 * ```
 */
export function useMicAudioLevel(enabled: boolean): MicAudioLevelState {
  const [state, setState] = useState<MicAudioLevelState>({
    level: 0,
    isActive: false,
    error: null,
  });

  // Hold refs so teardown is always accessible from cleanup callbacks
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animFrameRef = useRef<number | null>(null);
  // Guard against setState after unmount
  const mountedRef = useRef(true);

  const teardown = useCallback(() => {
    if (animFrameRef.current !== null) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      // close() returns a promise; fire-and-forget is fine here
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (mountedRef.current) {
      setState({ level: 0, isActive: false, error: null });
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      teardown();
      return;
    }

    let cancelled = false;

    async function start() {
      // Browser support guard
      if (
        typeof window === 'undefined' ||
        !navigator.mediaDevices?.getUserMedia
      ) {
        if (mountedRef.current) {
          setState({ level: 0, isActive: false, error: 'Microphone not supported in this browser.' });
        }
        return;
      }

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 44100,
          },
        });
      } catch (err) {
        logger.error('useMicAudioLevel: getUserMedia failed', err);
        if (mountedRef.current && !cancelled) {
          setState({
            level: 0,
            isActive: false,
            error: 'Could not access microphone. Please check your permissions.',
          });
        }
        return;
      }

      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      streamRef.current = stream;

      // Build AudioContext → Source → Analyser chain
      const AudioContextCtor =
        window.AudioContext ||
        (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

      if (!AudioContextCtor) {
        stream.getTracks().forEach((t) => t.stop());
        if (mountedRef.current) {
          setState({ level: 0, isActive: false, error: 'AudioContext not supported.' });
        }
        return;
      }

      const ctx = new AudioContextCtor();
      audioContextRef.current = ctx;

      // Resume if the browser created the context in suspended state
      if (ctx.state === 'suspended') {
        try {
          await ctx.resume();
        } catch (e) {
          logger.warn('useMicAudioLevel: AudioContext.resume() failed', e);
        }
      }

      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      if (mountedRef.current && !cancelled) {
        setState({ level: 0, isActive: true, error: null });
      }

      function tick() {
        if (cancelled || !mountedRef.current) return;

        analyser.getByteFrequencyData(dataArray);

        // RMS of frequency magnitudes, normalised + scaled to 0–1
        const scaled = computeMicLevel(dataArray);

        if (mountedRef.current) {
          setState((prev) => ({ ...prev, level: scaled }));
        }

        animFrameRef.current = requestAnimationFrame(tick);
      }

      animFrameRef.current = requestAnimationFrame(tick);
    }

    start();

    return () => {
      cancelled = true;
      teardown();
    };
  }, [enabled, teardown]);

  return state;
}
